import React from 'react';
import { Download, Upload, Eye, EyeOff, Save, Undo2, Redo2, ArrowLeft, Leaf } from 'lucide-react';
import { useEditor } from '../store/EditorContext';
import { FieldType, InputControl, DataSourceItem, FormDefinition, Field } from '../types';
import toast from 'react-hot-toast';

export const Header: React.FC = () => {
  const { state, dispatch, onSave, onBack } = useEditor();

  const handleSave = () => {
      if (onSave) {
          onSave(state.form);
      } else {
          toast.success('Form saved (mock)');
      }
  };

  const handleExport = () => {
    try {
        const exportData = {
          Name: state.form.name,
          Data: {
            FormFields: state.form.fields.map(f => {
                const extensions = { ...f.extensions };
                if (f.inputControl) extensions.input = f.inputControl;
                if (f.datasourceString) extensions.datasource = f.datasourceString;
                
                let values = null;
                if (f.dataSource && f.dataSource.length > 0) {
                    values = f.dataSource.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {});
                }

                if (f.modifiers && f.modifiers.length > 0) {
                    extensions.visualModifiers = JSON.stringify(f.modifiers);
                }

                return {
                  Id: f.id,
                  Label: f.label,
                  Required: f.validation?.required || false,
                  Disabled: f.disabled || false,
                  Hidden: f.hidden || false,
                  ValueType: f.type,
                  DefaultValue: f.defaultValue || "",
                  Modifier: "",
                  Values: values,
                  Extensions: extensions
                };
            }),
            Modifiers: state.form.modifiersScript || ""
          }
        };

        const jsonContent = JSON.stringify(exportData, null, 2);
        const dataStr = "data:application/json;charset=utf-8," + encodeURIComponent(jsonContent);
        
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute("download", `${state.form.name.replace(/\s+/g, '_')}_${new Date().getTime()}.json`);
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
        toast.success('Form exported successfully');
    } catch (e) {
        console.error(e);
        toast.error('Failed to export form');
    }
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
          try {
              const text = event.target?.result as string;
              tryParseJSON(text);
          } catch (err) {
              console.error(err);
              toast.error('Failed to parse file');
          }
      };
      reader.readAsText(file);
      e.target.value = '';
  };

  const tryParseJSON = (str: string) => {
      try {
          const json = JSON.parse(str);
          
          if (!json.Data || !Array.isArray(json.Data.FormFields)) {
               if (json.fields && Array.isArray(json.fields)) {
                   dispatch({ type: 'LOAD_FORM', payload: json });
                   toast.success('Legacy form imported');
                   return;
               }
               throw new Error('Invalid format');
          }

          const newFields: Field[] = json.Data.FormFields.map((ext: any) => {
              const extensions = ext.Extensions || {};
              const inputControl = extensions.input as InputControl || InputControl.TEXT;
              const datasourceString = extensions.datasource;
              
              let dataSource: DataSourceItem[] = [];
              if (ext.Values) {
                  dataSource = Object.entries(ext.Values).map(([key, value]) => ({
                      key,
                      value: String(value)
                  }));
              }

              let modifiers = [];
              if (extensions.visualModifiers) {
                  try {
                      modifiers = JSON.parse(extensions.visualModifiers);
                  } catch (e) { console.warn('Failed to parse visual modifiers', e); }
              }

              return {
                  id: ext.Id,
                  label: ext.Label,
                  type: ext.ValueType as FieldType,
                  inputControl: inputControl,
                  validation: { required: ext.Required },
                  disabled: ext.Disabled,
                  hidden: ext.Hidden,
                  defaultValue: ext.DefaultValue,
                  dataSource: dataSource,
                  datasourceString: datasourceString,
                  modifiers: modifiers,
                  extensions: extensions,
                  groupId: undefined
              };
          });

          const newForm: FormDefinition = {
              id: `form_${Date.now()}`,
              name: json.Name || 'Imported Form',
              version: '1.0',
              fields: newFields,
              groups: [],
              modifiersScript: json.Data.Modifiers
          };

          dispatch({ type: 'LOAD_FORM', payload: newForm });
          toast.success('Form imported successfully');

      } catch (e) {
          console.error(e);
          toast.error('Invalid JSON structure');
      }
  };

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-50">
      <div className="flex items-center gap-4">
        {onBack && (
            <button 
                onClick={onBack}
                className="p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors mr-1"
                title="Back"
            >
                <ArrowLeft size={20} />
            </button>
        )}

        <div className="flex items-center gap-3">
            {/* Logo: Fallback to Icon if Image fails, or just use Icon as "Clean Slate" style suggests */}
            <div className="flex items-center gap-2 text-teal-600">
                 <Leaf className="h-6 w-6" />
                 <span className="font-bold text-slate-900 tracking-tight text-lg hidden md:block">Peak<span className="text-slate-500">Form</span></span>
            </div>
            <div className="h-6 w-px bg-slate-200" />
        </div>
        
        <div className="flex flex-col">
             <input
                value={state.form.name}
                onChange={(e) => dispatch({ type: 'SET_FORM_NAME', payload: e.target.value })}
                className="text-sm font-bold text-slate-900 bg-transparent hover:bg-slate-50 rounded px-2 py-0.5 outline-none border border-transparent focus:border-teal-500 transition-colors placeholder-slate-400"
                placeholder="Form Name"
             />
             <span className="text-[10px] text-slate-500 font-mono px-2">v{state.form.version}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center bg-slate-50 border border-slate-100 rounded-lg p-0.5 mr-4">
            <button
                onClick={() => dispatch({ type: 'UNDO' })}
                disabled={state.historyIndex <= 0}
                className="p-2 text-slate-400 hover:text-teal-600 hover:bg-white rounded-md disabled:opacity-30 transition-all"
                title="Undo"
            >
                <Undo2 size={16} />
            </button>
             <button
                onClick={() => dispatch({ type: 'REDO' })}
                disabled={state.historyIndex >= state.history.length - 1}
                className="p-2 text-slate-400 hover:text-teal-600 hover:bg-white rounded-md disabled:opacity-30 transition-all"
                title="Redo"
            >
                <Redo2 size={16} />
            </button>
        </div>

        <button
          onClick={() => dispatch({ type: 'TOGGLE_PREVIEW', payload: !state.isPreviewMode })}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            state.isPreviewMode 
              ? 'bg-teal-50 text-teal-700 ring-1 ring-teal-200' 
              : 'text-slate-500 hover:text-teal-600 hover:bg-teal-50'
          }`}
        >
          {state.isPreviewMode ? <EyeOff size={16} /> : <Eye size={16} />}
          {state.isPreviewMode ? 'Exit' : 'Preview'}
        </button>

        <div className="h-6 w-px bg-slate-200 mx-2" />
        
        <label className="flex items-center gap-2 p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg cursor-pointer transition-colors" title="Import JSON">
            <Upload size={18} />
            <input type="file" accept=".json" onChange={handleImport} className="hidden" />
        </label>

        <button
          onClick={handleExport}
          className="p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
          title="Export JSON"
        >
          <Download size={18} />
        </button>
        
        <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm shadow-teal-600/20 ml-2"
        >
            <Save size={16} />
            Save
        </button>
      </div>
    </header>
  );
};